import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { usePrevious } from 'hooks';
import { useSelector } from 'react-redux';
import { Autocomplete } from 'components/FormFields';
import { vehicleActions } from 'state/modules/vehicles';
import { truncateKeywordSpaces } from 'utils/utils';
import CircularProgress from '@material-ui/core/CircularProgress';

const VehiclesAssetClassesSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  form: { errors, touched, setFieldValue },
  disabled,
  required,
  onHide,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const { assetClasses, assetClassesLoading } = useSelector((state) => state.vehicles);
  const prevvalue = usePrevious(value);

  const fetchVehiclesAssetClasses = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(vehicleActions.fetchVehiclesAssetClasses(newParams));
    }, 1000),
    [dispatch, setParams]
  );

  useEffect(() => {
    fetchVehiclesAssetClasses(params);
  }, []); // eslint-disable-line

  useEffect(() => {
    // If only one assetClass, auto-select it
    if (assetClasses.length === 1) {
      setFieldValue(name, assetClasses[0].key);
      setDefaultValue(assetClasses[0]);
      if (onHide) onHide(); // <-- Notify parent to hide
    } else if (value !== prevvalue || !defaultValue) {
      const defaultVal = assetClasses.find((el) => el.key === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      }
    } else if (!value && defaultValue) {
      setDefaultValue(null);
    }
  }, [value, assetClasses]); // eslint-disable-line

  const handleFetchVehiclesAssetClasses = () => {
    if (assetClasses.length === 0 && !assetClassesLoading && !params?.keyword)
      dispatch(vehicleActions.fetchVehiclesAssetClasses(params));
  };

  if (assetClassesLoading) {
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 56 }}
      >
        <CircularProgress size={24} color="primary" />
      </div>
    );
  }

  if (assetClasses.length === 1) {
    return null;
  }

  return (
    <Autocomplete
      label={label}
      margin="normal"
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      required={required}
      clearOnBlur={false}
      value={defaultValue}
      options={assetClasses}
      loading={assetClassesLoading}
      id="vehiclesAssetClassesSelect"
      onHighlightChange={handleFetchVehiclesAssetClasses}
      getOptionLabel={(option) => option.message || ''}
      onChange={(ev, val) => setFieldValue(name, val?.key || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.key === value.key}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      onInputChange={(ev, val) => fetchVehiclesAssetClasses({ ...params, keyword: val })}
    />
  );
};

VehiclesAssetClassesSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
  onHide: PropTypes.func,
};

VehiclesAssetClassesSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default VehiclesAssetClassesSelect;
